import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { Loading } from '@carbon/react';
import { useAuthSession, useOrganizationRole } from 'src/hooks/useAuthSession';
import { useOnboardingStatus } from 'src/hooks/useOnboardingStatus';
import { PENDING_INVITATION_KEY } from 'src/pages/AcceptInvitation';
import { orgRestaurants, orgRestaurantBranches } from 'src/services/apis/tenantRequests';
import { TenantContextProvider } from './TenantContextProvider';
import { resolveContext } from './resolveContext';
import { resolveShim, isUuid, ShimParams } from './shimResolver';
import { readTenantCookie, writeTenantCookie } from './tenantCookie';
import { ALL_BRANCHES, OnboardingStatus, OrganizationRole } from './types';

export const ContextLoader = () => {
  const params = useParams();
  const location = useLocation();
  const { session, isPending: sessionPending, isVerified } = useAuthSession();
  const organizationRole = useOrganizationRole();
  const { data: status, isLoading: statusPending } = useOnboardingStatus(Boolean(session));

  if (sessionPending || (session && statusPending)) {
    return <Loading withOverlay description="Loading workspace" />;
  }

  const pendingInvitation = sessionStorage.getItem(PENDING_INVITATION_KEY);
  if (session && isVerified && pendingInvitation) {
    return <Navigate to={`/accept-invitation/${pendingInvitation}`} replace />;
  }

  const canAggregate = organizationRole === 'owner' || organizationRole === 'admin';
  const sessionValue = session ? { emailVerified: isVerified, organizationRole } : null;

  // Shim mode: an /o/... route param set is present.
  const isShim = Boolean(params.orgSlug);

  if (isShim) {
    const leaf = params['*'] || undefined;
    return (
      <ShimLoader
        session={sessionValue}
        status={status ?? null}
        canAggregate={canAggregate}
        params={{
          orgSlug: params.orgSlug!,
          restaurantId: params.restaurantId,
          branchId: params.branchId,
          leaf,
        }}
      />
    );
  }

  const resolution = resolveContext({
    session: sessionValue,
    status: status ?? null,
    cookie: readTenantCookie(),
    currentPath: location.pathname + location.search,
    hasOrgLeaf:
      location.pathname.startsWith('/settings/organization') ||
      location.pathname.startsWith('/settings/integrations') ||
      location.pathname.startsWith('/profile'),
  });

  if (resolution.kind === 'redirect') {
    return <Navigate to={resolution.to} replace />;
  }

  writeTenantCookie(resolution.context);

  return (
    <TenantContextProvider
      key={`${resolution.context.orgSlug}:${resolution.context.restaurantId}:${resolution.context.branchId}`}
      initial={resolution.context}
    >
      <Outlet />
    </TenantContextProvider>
  );
};

const ShimLoader = ({
  session,
  status,
  canAggregate,
  params,
}: {
  session: { emailVerified: boolean; organizationRole: OrganizationRole | null } | null;
  status: OnboardingStatus | null;
  canAggregate: boolean;
  params: ShimParams;
}) => {
  const location = useLocation();
  const needsRestaurantMap = Boolean(params.restaurantId) && !isUuid(params.restaurantId!);
  const needsBranchMap = Boolean(params.branchId) && params.branchId !== ALL_BRANCHES && !isUuid(params.branchId!);

  const { data: restaurants, isLoading: restaurantsLoading } = useQuery({
    queryKey: ['shim-restaurants', params.orgSlug],
    queryFn: () => orgRestaurants({ orgSlug: params.orgSlug }),
    enabled: Boolean(session) && needsRestaurantMap,
  });
  const matchedRestaurant = restaurants?.data?.find(
    (restaurant) => restaurant.id === params.restaurantId || restaurant.code === params.restaurantId
  );
  const restaurantId = isUuid(params.restaurantId ?? '') ? params.restaurantId : matchedRestaurant?.id;

  const { data: branches, isLoading: branchesLoading } = useQuery({
    queryKey: ['shim-branches', restaurantId],
    queryFn: () => orgRestaurantBranches({ orgSlug: params.orgSlug, restaurantId: restaurantId! }),
    enabled: Boolean(session) && needsBranchMap && Boolean(restaurantId),
  });
  const branchId =
    params.branchId === ALL_BRANCHES || isUuid(params.branchId ?? '')
      ? params.branchId
      : branches?.data?.find((branch) => branch.code === params.branchId)?.id;

  const codeMapPending =
    Boolean(session) &&
    ((needsRestaurantMap && restaurantsLoading) || (needsBranchMap && Boolean(restaurantId) && branchesLoading));

  if (codeMapPending) {
    return <Loading withOverlay description="Loading workspace" />;
  }

  // A code that mapped to nothing must not look like "the URL had no such segment" —
  // otherwise the deep link silently resolves to the previous cookie's context.
  const unresolved = (needsRestaurantMap && !restaurantId) || (needsBranchMap && !branchId);

  const result = resolveShim({
    session,
    status,
    canAggregate,
    unresolved,
    params: { ...params, restaurantId, branchId },
  });

  if (result.kind === 'redirect') {
    // Deep links keep their query string across the shim redirect; sign-in targets
    // already carry their own returnTo query.
    const to = result.to.includes('?') ? result.to : `${result.to}${location.search}`;
    if (to.startsWith('/sign-in')) {
      return <Navigate to={to} replace />;
    }
    // Persist the resolved context so the short route renders directly.
    if (restaurantId && branchId) {
      writeTenantCookie({ orgSlug: params.orgSlug, restaurantId, branchId });
    }
    return <Navigate to={to} replace />;
  }
  return <Navigate to="/" replace />;
};
